'use strict';

import './component.scss';
import offset from 'components/complement/offset';
import Utils from 'components/utils';

const ARIA_HIDDEN = 'aria-hidden';

class Tooltip {
    constructor() {
        this.body = document.querySelector('body');
        this.tooltipId = 0;
        this.markup;
        this.defaultClasses = 'tooltip tooltip-js';
        this.defaultClassesArr = this.splitClasses(this.defaultClasses);
        this.tooltipAccessibleNameFix = document.documentElement.dataset.tooltipAccessibleNameFix;
    }

    init() {
        this.createMarkup();
        this.initNewTooltips();
    }

    createMarkup() {
        const roleAttr = this.tooltipAccessibleNameFix ? '' : ' role="tooltip"';
        this.body.insertAdjacentHTML('beforeend', `<span id="main-tooltip"${roleAttr} class="${this.defaultClasses}"></span>`);
        this.markup = this.body.querySelector('.tooltip-js');
        this.markup.setAttribute(ARIA_HIDDEN, 'true');
    }

    show(tooltipHoverElement) {
        if (!tooltipHoverElement.dataset.tooltipTitle) {
            return;
        }
        let tooltipHoverElementOffset = offset(tooltipHoverElement),
            position = tooltipHoverElement.dataset.position,
            alignment = tooltipHoverElement.dataset.alignment,
            tooltipTemplateClasses = tooltipHoverElement.dataset.templateClasses,
            additionalClasses = tooltipTemplateClasses ? this.splitClasses(tooltipHoverElement.dataset.templateClasses) : null;

        this.markup.innerHTML = tooltipHoverElement.dataset.tooltipTitle;
        if (this.tooltipAccessibleNameFix) {
            this.markup.setAttribute('role', 'tooltip');
            this.markup.removeAttribute(ARIA_HIDDEN);
        }

        if (additionalClasses) {
            this.addClasses(additionalClasses);
        }

        this.setBottomPosition(tooltipHoverElement, tooltipHoverElementOffset);

        switch(alignment) {
            case 'center':
                this.setHorizontalCenter(tooltipHoverElement, tooltipHoverElementOffset);
                break;
            case 'left':
                this.setHorizontalLeft(tooltipHoverElementOffset);
                break;
            default:
                this.setHorizontalCenter(tooltipHoverElement, tooltipHoverElementOffset);
        }

        this.markup.classList.add('tooltip-display');
    }

    setBottomPosition(tooltipHoverElement, tooltipHoverElementOffset) {
        this.markup.style.top = tooltipHoverElementOffset.top + tooltipHoverElement.offsetHeight + 7 + 'px';
    }

    setHorizontalCenter(tooltipHoverElement, tooltipHoverElementOffset) {
        this.markup.style.left = tooltipHoverElementOffset.left + (tooltipHoverElement.offsetWidth / 2) + 'px';
        this.markup.classList.add('tooltip--center-horizontal');
    }

    setHorizontalLeft(tooltipHoverElementOffset) {
        this.markup.style.left = tooltipHoverElementOffset.left + 'px';
    }

    hide() {
        this.clearClasses(this.markup);
        this.markup.style.top = 0;
        this.markup.style.left = 0;
        if (this.tooltipAccessibleNameFix) {
            this.markup.removeAttribute('role');
            this.markup.setAttribute(ARIA_HIDDEN, 'true');
        }
        this.addClasses(this.defaultClassesArr);
    }

    addClasses(arr) {
        for (let element of arr) {
            this.markup.classList.add(element);
        }
    }

    clearClasses(element) {
        element.setAttribute('class', '');
    }

    splitClasses(string) {
        let arr = string.split(' ');

        return arr;
    }

    isFullyVisible(element) {
        const rect = element.getBoundingClientRect();
        // Find the nearest scrollable parent
        let parent = element.parentElement;
        while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            const overflows = [style.overflow, style.overflowY, style.overflowX];
            if (overflows.some(val => val === 'auto' || val === 'scroll')) {
                break;
            }
            parent = parent.parentElement;
        }
        // If no scrollable parent found, element is not clipped and should always show
        if (!parent || parent === document.body) {
            return true;
        }
        const containerRect = parent.getBoundingClientRect();
        // Check if all sides of the target are within the container
        return (
            rect.top >= containerRect.top &&
            rect.left >= containerRect.left &&
            rect.bottom <= containerRect.bottom &&
            rect.right <= containerRect.right
        );
    }

    attachListeners(element) {
        let _this = this,
            id = 'tooltip-' + this.tooltipId,
            trigger = element.querySelector('.tooltip-trigger');

        // Set id to the element to be easy separated from dynamacilly loaded elements
        element.dataset.tooltip = this.tooltipId;
        element.id = id;

        // Set aria-describedby in terms to be read by SR properly
        if (trigger) {
            // When the element is svg the aria-descriedby is not read properly with JAWS
            // It's not needed to use describedby because the svg doesn't have any label
            if (trigger.tagName == 'svg') {
                trigger.setAttribute(ARIA_HIDDEN, 'true');
            } else {
                trigger.setAttribute('aria-describedby', id);
            }
        }

        this.tooltipId++;

        let startDrag = false;
        const tooltipsFeatureToggle = document.documentElement.dataset.afrNoIconIndicationInFareCardsAnymoreFix;

        element.addEventListener('mouseenter', function(){
            // Fare button tooltips should always show
            const isFareTooltip = element.classList.contains('fare-button-footer-fare-tooltip');
            if (isFareTooltip) {
                _this.show(element);
            }
            else {
                if (_this.isFullyVisible(element)) {
                    if (tooltipsFeatureToggle) {
                        if (!startDrag) {
                            _this.show(element);
                        }
                    } else {
                        _this.show(element);
                    }
                } else {
                    _this.hide();
                }
            }
        });

        element.addEventListener('mousedown', function(){
            if (tooltipsFeatureToggle) {
                startDrag = true;
                _this.hide();
            }
        });

        element.addEventListener('mousemove', function(){
            if (tooltipsFeatureToggle) {
                if (startDrag) {
                    element.style.pointerEvents = "none";
                    _this.hide();
                }
            }
        });

        document.addEventListener('mouseup', function(){
            if (tooltipsFeatureToggle) {
                if (startDrag) {
                    element.style.pointerEvents = null;
                    startDrag = false;
                }
            }
        });

        element.addEventListener('mouseleave', function(){
            if (tooltipsFeatureToggle) {
                if (!startDrag) {
                    _this.hide();
                }
            } else {
                _this.hide();
            }
        });
    }

    initNewTooltips() {
        let tooltipHoverElements = document.querySelectorAll('[data-tooltip=""]');

        // Tooltips elements works only on desktop (will be improved)
        if (tooltipHoverElements && Utils.breakpoint.match('xlarge') && !Utils.isMobile()) {
            for (let tooltipHoverElement of tooltipHoverElements) {
                if (!Utils.isIE()) {
                    this.attachListeners(tooltipHoverElement);
                } else {
                    // Skip elements which are in template tag only on IE.
                    if (!tooltipHoverElement.closest('template')) {
                        this.attachListeners(tooltipHoverElement);
                    }
                }
            }
        }
    }
}

export { Tooltip as default };
